/**
 * Public Delivery Confirmation Page
 * Receiver scans QR code → sees invoice details & temp compliance → confirms receipt
 */
import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { publicApi } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Shield, Thermometer, Package, Truck, CheckCircle,
  XCircle, AlertTriangle, Clock, MapPin, Loader2, User
} from 'lucide-react';

export default function DeliveryConfirmPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [receiverName, setReceiverName] = useState('');
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await publicApi.getDeliveryConfirmData(token);
      setData(res.data);
      if (res.data.already_confirmed) setConfirmed(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired link');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) fetchData();
  }, [token, fetchData]);

  const handleConfirm = async () => {
    try {
      setConfirming(true);
      const res = await publicApi.confirmDeliveryPublic(token, {
        receiver_name: receiverName.trim() || undefined
      });
      setConfirmed(true);
      setData(prev => ({
        ...prev,
        already_confirmed: true,
        verified_at: res.data.verified_at,
        verified_by: res.data.verified_by
      }));
      toast.success('Delivery confirmed successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Confirmation failed');
    } finally {
      setConfirming(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-teal-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading delivery details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Card className="bg-slate-800 border-slate-700 max-w-md w-full">
          <CardContent className="p-8 text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Link Invalid</h2>
            <p className="text-slate-400">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const report = data?.compliance_report;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4" data-testid="delivery-confirm-page">
      <div className="max-w-lg mx-auto space-y-5 py-6">
        {/* Header */}
        <div className="text-center">
          <div className="flex items-center justify-center gap-1 mb-1">
            <h1 className="text-xl font-bold text-white">
              Track<span className="text-red-500">0</span>tic<sup className="text-[0.5em] align-super opacity-60">™</sup>
            </h1>
          </div>
          <p className="text-slate-400 text-sm">Delivery Confirmation</p>
        </div>

        {/* Confirmed Banner */}
        {confirmed && (
          <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 text-center" data-testid="confirmed-banner">
            <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-2" />
            <p className="text-green-400 font-semibold text-lg">Delivery Confirmed</p>
            <p className="text-slate-400 text-sm mt-1">
              {data?.verified_by && `By ${data.verified_by} · `}{formatDate(data?.verified_at)}
            </p>
          </div>
        )}

        {/* Shipment Info */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-white flex items-center gap-2 text-base">
              <Package className="w-5 h-5 text-teal-500" />
              Shipment Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-slate-400 block">Invoice</span>
                <p className="text-white font-medium">{data?.invoice_number || '—'}</p>
              </div>
              <div>
                <span className="text-slate-400 block">Status</span>
                <Badge className={confirmed ? 'bg-green-500' : 'bg-blue-500'}>
                  {confirmed ? 'Delivered' : data?.status || 'In Transit'}
                </Badge>
              </div>
              <div>
                <span className="text-slate-400 block">From</span>
                <p className="text-white">{data?.supplier_name || data?.tenant_name || '—'}</p>
              </div>
              <div>
                <span className="text-slate-400 block">To</span>
                <p className="text-white">{data?.customer_name || '—'}</p>
              </div>
              {data?.item_description && (
                <div className="col-span-2">
                  <span className="text-slate-400 block">Items</span>
                  <p className="text-white">{data.item_description}{data.item_count ? ` (${data.item_count} items)` : ''}</p>
                </div>
              )}
              {data?.delivery_address && (
                <div className="col-span-2">
                  <span className="text-slate-400 block flex items-center gap-1"><MapPin className="w-3 h-3" />Delivery Address</span>
                  <p className="text-white text-sm">{data.delivery_address}</p>
                </div>
              )}
            </div>

            <div className="border-t border-slate-700 pt-3 grid grid-cols-3 gap-3 text-sm">
              <div>
                <span className="text-slate-400 block">Trip</span>
                <p className="text-white">{data?.trip_number || '—'}</p>
              </div>
              <div>
                <span className="text-slate-400 block">Driver</span>
                <p className="text-white">{data?.driver_name || '—'}</p>
              </div>
              <div>
                <span className="text-slate-400 block">Vehicle</span>
                <p className="text-white">{data?.vehicle_number || '—'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Temperature Compliance */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-white flex items-center gap-2 text-base">
              <Thermometer className="w-5 h-5 text-teal-500" />
              Temperature Compliance
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data?.temp_range && (
              <p className="text-slate-400 text-xs mb-3">Required range: {data.temp_range}</p>
            )}
            {report ? (
              <>
                <div className="grid grid-cols-4 gap-2 text-center">
                  <div className="bg-slate-900 p-3 rounded-lg">
                    <p className="text-slate-400 text-xs">Min</p>
                    <p className="text-blue-400 font-bold">{report.temp_min?.toFixed(1) || '—'}°C</p>
                  </div>
                  <div className="bg-slate-900 p-3 rounded-lg">
                    <p className="text-slate-400 text-xs">Max</p>
                    <p className="text-red-400 font-bold">{report.temp_max?.toFixed(1) || '—'}°C</p>
                  </div>
                  <div className="bg-slate-900 p-3 rounded-lg">
                    <p className="text-slate-400 text-xs">Avg</p>
                    <p className="text-teal-400 font-bold">{report.temp_avg?.toFixed(1) || '—'}°C</p>
                  </div>
                  <div className="bg-slate-900 p-3 rounded-lg">
                    <p className="text-slate-400 text-xs">Breaches</p>
                    <p className={`font-bold ${report.breach_count > 0 ? 'text-red-400' : 'text-green-400'}`}>
                      {report.breach_count}
                    </p>
                  </div>
                </div>
                <div className="mt-3 text-center">
                  <Badge className={
                    report.compliance_status === 'compliant' ? 'bg-green-500' :
                    report.compliance_status === 'partial' ? 'bg-amber-500' : 'bg-red-500'
                  }>
                    {(report.compliance_status || 'pending').toUpperCase()}
                  </Badge>
                </div>
              </>
            ) : (
              <div className="text-center py-4 text-slate-500 text-sm">
                <Thermometer className="w-6 h-6 mx-auto mb-2 opacity-50" />
                Temperature report not yet available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Confirm Button */}
        {!confirmed && (
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-5 space-y-4">
              <h3 className="text-white font-semibold flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-teal-500" />
                Confirm Receipt
              </h3>
              <p className="text-slate-400 text-sm">
                By confirming, you acknowledge that you have received the shipment.
              </p>
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Your Name (optional)</label>
                <Input
                  value={receiverName}
                  onChange={(e) => setReceiverName(e.target.value)}
                  placeholder="Enter your name"
                  className="bg-slate-900 border-slate-700 text-white"
                  data-testid="confirm-receiver-name"
                />
              </div>
              <Button
                onClick={handleConfirm}
                disabled={confirming}
                className="w-full bg-teal-600 hover:bg-teal-700 text-white h-12 text-base"
                data-testid="confirm-delivery-btn"
              >
                {confirming ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Confirming...</>
                ) : (
                  <><CheckCircle className="w-5 h-5 mr-2" />Confirm Delivery Received</>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Delivery timestamp */}
        {confirmed && data?.delivery_timestamp && (
          <div className="text-center text-slate-500 text-xs flex items-center justify-center gap-1">
            <Clock className="w-3 h-3" />
            Delivered: {formatDate(data.delivery_timestamp)}
          </div>
        )}

        <p className="text-center text-slate-600 text-xs pt-2">
          Powered by Track<span className="text-red-500">0</span>tic<sup className="text-[0.5em] align-super opacity-60">™</sup> | track verified
        </p>
      </div>
    </div>
  );
}
